import {
  Cross2Icon,
  LightningBoltIcon,
  MagnifyingGlassIcon,
} from "@radix-ui/react-icons";
import "components/Dashboard/PeoplePanel/index.css";
import Pager from "components/Dashboard/PeoplePanel/Pager";
import { Select } from "components/Select";
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ActivityApproveAll from "./ActivityApproveAll";
import { actionWords } from "./ActivityPill";
import ActivityTabDone from "./ActivityTabDone";
import ActivityTabWaiting from "./ActivityTabWaiting";
import {
  LoadingRows,
  useActivityPage,
  useApprovals,
  useAutomations,
} from "./hooks";
import type { ActivityAction } from "./types";

/** Activity's two lists. */
export type ActivityView = "waiting" | "done";

interface ViewOption {
  value: ActivityView;
  label: string;
}

const VIEWS: ViewOption[] = [
  { value: "waiting", label: "Waiting for approval" },
  { value: "done", label: "Done" },
];

const PAGE_SIZE = 25;

/** The address's parameter for one automation's History (a lane's History link). */
const AUTOMATION = "automation";

/** Each list's filter pills: the kinds of action its rows can be. */
const ACTIONS: Record<ActivityView, ActivityAction[]> = {
  waiting: [
    "message",
    "task",
    "crm_move",
    "crm_field",
    "close_task",
    "assign_tutor",
    "add_note",
  ],
  done: [
    "message",
    "task",
    "crm_move",
    "crm_field",
    "close_task",
    "assign_tutor",
    "add_note",
    "wait",
  ],
};

/** The orange count of what waits for approval, as the page's tab shows it. */
function WaitingCount({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <span className="rounded-full bg-orange-500 px-1.5 text-xs font-semibold leading-5 text-white">
      {count > 99 ? "99+" : count}
    </span>
  );
}

/** Waiting for approval or Done, in the app's own dropdown, with the count of what waits. */
function ViewSelect({
  view,
  waiting,
  onView,
}: {
  view: ActivityView;
  waiting: number;
  onView: (view: ActivityView) => void;
}) {
  return (
    <div className="w-60">
      <Select
        aria-label="Activity"
        isSearchable={false}
        options={VIEWS}
        value={VIEWS.find((option) => option.value === view)}
        onChange={(option: ViewOption | null) => option && onView(option.value)}
        formatOptionLabel={(option: ViewOption) => (
          <span className="inline-flex items-center gap-1.5">
            {option.label}
            {option.value === "waiting" && <WaitingCount count={waiting} />}
          </span>
        )}
      />
    </div>
  );
}

/**
 * Show (N): a pill for each kind of action, with how many the list (or its
 * search) holds, as the dashboard's "Show people with" pills. Picked ones
 * keep only those kinds, and N says how many that is (everything when none
 * is). Picking pills changes no pill's number.
 */
function ActionFilter({
  view,
  counts,
  total,
  most,
  picked,
  onChange,
}: {
  view: ActivityView;
  counts: Record<ActivityAction, number>;
  /** How many the picked pills keep. */
  total: number;
  /** The most total can be: its room is kept, so the pills stay put. */
  most: number;
  picked: ActivityAction[];
  onChange: (picked: ActivityAction[]) => void;
}) {
  return (
    <div className="people-missing !mb-0" role="group" aria-label="Show">
      <span className="people-missing__label">
        Show{" "}
        <span
          className="people-missing__total"
          style={{ minWidth: `${String(most).length + 2}ch` }}
        >
          ({total})
        </span>
      </span>
      {ACTIONS[view].map((action) => {
        const on = picked.includes(action);
        return (
          <button
            key={action}
            type="button"
            aria-pressed={on}
            className={`people-missing__chip${
              on ? " people-missing__chip--on" : ""
            }`}
            onClick={() =>
              onChange(
                on ? picked.filter((a) => a !== action) : [...picked, action]
              )
            }
          >
            {actionWords(action, view === "done")}
            <span className="people-missing__count">{counts[action] ?? 0}</span>
          </button>
        );
      })}
    </div>
  );
}

/** The automation whose History this is, as its purple chip; × shows every automation's again. */
function AutomationChip({
  name,
  onClear,
}: {
  name: string;
  onClear: () => void;
}) {
  return (
    <span className="inline-flex min-w-0 max-w-[16rem] items-center gap-0.5">
      <span className="person-pill person-pill--purple" title={name}>
        <LightningBoltIcon
          className="-mt-px mr-0.5 inline h-2.5 w-2.5"
          aria-hidden
        />
        {name}
      </span>
      <button
        type="button"
        className="rounded-full p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
        aria-label="Show all automations"
        title="Show all automations"
        onClick={onClear}
      >
        <Cross2Icon width="10" height="10" />
      </button>
    </span>
  );
}

/**
 * The Activity tab: Waiting for approval or Done (the app's dropdown, with
 * the count of what waits), the filter pills by action, Approve all on
 * Waiting for approval, and one table for either, a page at a time. The
 * search, the pills and their counts are the backend's, for the whole
 * list. ?automation=<id> (a lane's History) keeps both lists to that
 * automation, with its chip; its × drops it.
 */
export default function ActivityTab({
  view,
  onView,
}: {
  view: ActivityView;
  onView: (view: ActivityView) => void;
}) {
  const { approvals } = useApprovals();
  const { automations, loading: automationsLoading } = useAutomations();
  const [params, setParams] = useSearchParams();
  const asked = params.get(AUTOMATION);
  const automationId = asked && /^\d+$/.test(asked) ? asked : undefined;
  const [input, setInput] = useState("");
  const [search, setSearch] = useState("");
  const [picked, setPicked] = useState<ActivityAction[]>([]);
  // Each list has its own pills: another list starts with none picked
  // (reset while rendering, so the old pills are never asked for it).
  const [pickedFor, setPickedFor] = useState(view);
  if (pickedFor !== view) {
    setPickedFor(view);
    setPicked([]);
  }
  // Each change of list, search, pills or automation starts on page 1.
  const listKey = `${view}|${search}|${picked.join(",")}|${automationId ?? ""}`;
  const [page, setPage] = useState(0);
  const [pageListKey, setPageListKey] = useState(listKey);
  if (pageListKey !== listKey) {
    setPageListKey(listKey);
    setPage(0);
  }

  // The search box drives the search 250 ms after the last keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setSearch(input.trim()), 250);
    return () => clearTimeout(timer);
  }, [input]);

  const clearAutomation = useCallback(
    () =>
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete(AUTOMATION);
          return next;
        },
        { replace: true }
      ),
    [setParams]
  );

  // An automation parameter that isn't an automation's id is dropped.
  useEffect(() => {
    if (asked !== null && !automationId) clearAutomation();
  }, [asked, automationId, clearAutomation]);

  const {
    page: found,
    current,
    error,
  } = useActivityPage({
    view,
    search,
    actions: picked,
    automationId,
    page,
    pageSize: PAGE_SIZE,
  });
  // The page shown is this list's; another list's waits (loading) instead.
  const shown = found && found.view === view ? found : null;
  const total = shown?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // A page left empty (its last row answered, say) moves to the last page there is.
  useEffect(() => {
    if (current && page > pageCount - 1) setPage(pageCount - 1);
  }, [current, page, pageCount]);

  const automationName = automationId
    ? automations.find((automation) => automation.id === automationId)?.name ??
      (automationsLoading ? null : "An automation that was deleted")
    : null;
  const filtering = !!search || picked.length > 0;
  // Nothing at all in this list (for this automation): its sentence, no pills.
  const empty = !!shown && total === 0 && !filtering;
  const most = shown
    ? ACTIONS[view].reduce((sum, action) => sum + shown.counts[action], 0)
    : 0;

  return (
    <div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <ViewSelect view={view} waiting={approvals.length} onView={onView} />
        {shown && !empty && (
          <ActionFilter
            view={view}
            counts={shown.counts}
            total={total}
            most={most}
            picked={picked}
            onChange={setPicked}
          />
        )}
        {automationName !== null && (
          <AutomationChip name={automationName} onClear={clearAutomation} />
        )}
        {view === "waiting" && shown && total > shown.leftOut && (
          <div className="ml-auto">
            <ActivityApproveAll
              query={{ search, actions: picked, automationId }}
              total={total - shown.leftOut}
              leftOut={shown.leftOut}
              disabled={!current}
            />
          </div>
        )}
      </div>

      {error ? (
        <p className="mt-4 text-sm text-red-600">{error}</p>
      ) : !shown ? (
        <LoadingRows />
      ) : empty ? (
        <p className="py-10 text-center text-sm text-gray-500">
          {view === "waiting"
            ? "Nothing is waiting for approval."
            : "Nothing has happened yet."}
        </p>
      ) : (
        <>
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              {total > PAGE_SIZE && (
                <Pager
                  page={Math.min(page, pageCount - 1)}
                  pageCount={pageCount}
                  onPage={setPage}
                  disabled={!current}
                />
              )}
            </div>
            <div className="people-panel__search min-w-[16rem]">
              <MagnifyingGlassIcon className="people-panel__search-icon" />
              <input
                type="text"
                className="input m-0 py-1.5 pl-9 pr-8"
                placeholder="Search by person or automation"
                aria-label="Search by person or automation"
                maxLength={100}
                autoComplete="off"
                value={input}
                onChange={(e) => setInput(e.target.value)}
              />
              {input && (
                <button
                  type="button"
                  className="people-panel__clear"
                  aria-label="Clear search"
                  onClick={() => {
                    setInput("");
                    setSearch("");
                  }}
                >
                  <Cross2Icon />
                </button>
              )}
            </div>
          </div>

          {total === 0 ? (
            <p className="py-10 text-center text-sm text-gray-500">
              Nothing matches.
            </p>
          ) : view === "waiting" ? (
            <ActivityTabWaiting items={shown.items} faded={!current} />
          ) : (
            <ActivityTabDone rows={shown.rows} faded={!current} />
          )}
        </>
      )}
    </div>
  );
}
